import { ICheckboxFilter } from "@landtechnologies/dtos";
import { searchStore } from "../store";
import { SearchState } from "react-migration/domains/search/typings/Search";
import { McSearchResultDTO, MultiCriteriaSearchArgsDTO } from "@landtechnologies/dtos";
import { ENVIRONMENT } from "src/js/util/environment";
import { ISearchFilter } from "react-migration/domains/search";
import { v4 as uuidv4 } from "uuid";
import isLocationFilter from "react-migration/domains/search/util/isLocationFilter";
import { updateDefaultFilters } from "react-migration/domains/search/components/FilterList/defaultFilters";

export const setSearchId = (searchId: SearchState["searchId"]) => {
  searchStore.searchId = searchId;
};

export const setResultsLoading = (resultsLoading: SearchState["searchResultsLoading"]) => {
  searchStore.searchResultsLoading = resultsLoading;
};
export const setSelectedItemsById = (selectedItemsById: SearchState["selectedItemsById"]) => {
  searchStore.selectedItemsById = selectedItemsById;
};
export const setMapDragged = (mapDragged: SearchState["mapDragged"]) => {
  searchStore.mapDragged = mapDragged;
};
export const setResultsSaved = (isSaved: boolean) => {
  searchStore.savedResults = isSaved;
};
export const setSavedResultsError = (msg: string) => {
  searchStore.savedResultsError = msg;
};
export const setPanSearchCheck = (panSearchCheck: SearchState["panSearchCheck"]) => {
  searchStore.panSearchCheck = panSearchCheck;
};
export const setSearchOnMapChange = (searchOnMapChange: SearchState["searchOnMapChange"]) => {
  searchStore.searchOnMapChange = searchOnMapChange;
};
export const setActiveTab = (activeTab: SearchState["activeTab"]) => {
  searchStore.activeTab = activeTab;
};
export const setSearchToDelete = (searchToDelete: SearchState["searchToDelete"]) => {
  searchStore.searchToDelete = searchToDelete;
};
export const setSearchResults = (searchResults?: McSearchResultDTO[]) => {
  searchStore.searchResults = searchResults;
};
export const executeSearch = (params?: MultiCriteriaSearchArgsDTO, skip?: boolean) => {
  if (skip) {
    return;
  }

  searchStore.lastRequest?.abort();
  searchStore.searchResultsLoading = true;
  searchStore.searchResultsError = undefined;
  searchStore.lastRequest = {
    params,
    abort: () => abortController.abort(),
  };
  const abortController = new AbortController();
  const url = `${ENVIRONMENT.MCSEARCH_SERVICE_URL}/search`;
  fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Accept-Endoding": "br",
    },
    body: JSON.stringify(params),
    signal: abortController.signal,
  })
    .then(async (res) => {
      if (res.status !== 200) {
        throw new Error(String(res.status));
      }
      return res.json();
    })
    .then((data) => {
      setSearchResults(data);
      // did have this in a .finally but when a new request was made mid-flight the aborted request would end up setting searchResultsLoading to false
      // in the tick after it was set to true a few lines above here
      searchStore.searchResultsLoading = false;
    })
    .catch((err) => {
      if (/the user aborted a request/i.test(err.message)) {
        // aborted requests => pas de probleme
        return;
      }
      setSearchResults();
      searchStore.searchResultsError = err.message;
      searchStore.searchResultsLoading = false;
      console.log(err.message);
    });
};

export function addFilter(filter: ISearchFilter) {
  searchStore.selectedFilters.push({
    ...structuredClone(filter),
    index: uuidv4(),
    updateFilterDialogIsVisible: true,
  });
}

export function removeFilter(filter: ISearchFilter) {
  searchStore.selectedFilters = searchStore.selectedFilters.filter((f) => f.index !== filter.index);
}

export function hideFilterUpdateDialog(filter: ISearchFilter) {
  searchStore.selectedFilters.forEach((f) => {
    if (f.index === filter.index) {
      f.updateFilterDialogIsVisible = false;
    }
  });
}

export function toggleFilterUpdateDialog(index?: string) {
  searchStore.selectedFilters.forEach((filter) => {
    if (index === filter.index) {
      filter.updateFilterDialogIsVisible = !filter.updateFilterDialogIsVisible;
    }
  });
}

export function editFilter(updatedFilter: Partial<ISearchFilter>) {
  if (ENVIRONMENT.APP_ENV === "development") {
    if (!updatedFilter.index) {
      console.warn("No index provided for filter update so nothing will be updated");
    }
  }

  searchStore.selectedFilters.forEach((filter: ISearchFilter) => {
    if (filter.index === updatedFilter.index) {
      for (const key of Object.keys(updatedFilter)) {
        // @ts-expect-error we are iterating over the keys of updatedFilter but typescript just sees it as a string not a keyof ISearchFilter
        filter[key] = updatedFilter[key];
        filter.updateFilterDialogIsVisible = false;
      }
    }
  });
}

export function clearFilters() {
  searchStore.selectedFilters = updateDefaultFilters();
}

export function setSelectedFilters(filters: ISearchFilter[]): void;
export function setSelectedFilters(
  filters: (previousFilters: ISearchFilter[]) => ISearchFilter[]
): void;
export function setSelectedFilters(
  filters: ISearchFilter[] | ((previousFilters: ISearchFilter[]) => ISearchFilter[])
): void {
  if (typeof filters === "function") {
    searchStore.selectedFilters = filters(searchStore.selectedFilters);
    return;
  }

  if (!filters.length || !filters.some(isLocationFilter)) {
    console.error("No location filter found in selected filters - not updating selected filters");
    return;
  }
  searchStore.selectedFilters = filters;
}

export const setBoundaryFilters = (boundaries: ICheckboxFilter[]) => {
  searchStore.boundaryFilters = boundaries;
};

export const clearBoundaryFilters = () => {
  const boundaries = [...searchStore.boundaryFilters];
  searchStore.boundaryFilters = boundaries.map((boundary) => ({ ...boundary, checked: false }));
};
